import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, RefreshCw } from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { PasswordField } from "@/shared/components/ui/password-field";
import { useToast } from "@/shared/hooks/use-toast";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { translateDeep } from "@/shared/utils/i18nHelper";
import { IntegrationBaseService } from "../services/integrationsBaseService";
import { useIntegrationBaseCache } from "../hooks/useIntegrationBaseCache";
import { IntegrationConfigFields } from "./IntegrationConfigFields";
import { IntegrationMetadataFields, missingRequiredMetadata } from "./IntegrationMetadataFields";
import { IntegrationPostInstallDialog } from "./IntegrationPostInstallDialog";
import { instanceKind, mcpOAuthOf } from "../utils/integrationKind";
import { DEFAULT_API_KEY_HEADER, mcpCredentials, needsToken } from "../utils/mcpServer";
import {
  entriesToModels,
  modelsToEntries,
  validateEntries,
  type ModelEntry,
} from "../utils/modelEntries";
import type {
  AuthField,
  FormField,
  Integration,
  IntegrationBase,
  IntegrationPostInstall,
  MetadataFieldDefinition,
} from "../types/integration";

interface IntegrationConnectionTabProps {
  integration: IntegrationBase;
  template: Integration | null;
}

/**
 * Aba Conexão (AB#87542). Para `api`, os campos de configuração e credencial que o template
 * declara — os mesmos do diálogo de criar — e, quando houver, o catálogo de modelos. Para
 * `mcp` (AB#87477), a URL do servidor, o modo de autenticação e o token ou o OAuth.
 *
 * Credencial nunca volta da api: o campo começa vazio, e vazio ao salvar quer dizer
 * "manter a atual".
 */
export const IntegrationConnectionTab = ({ integration, template }: IntegrationConnectionTabProps) => {
  return instanceKind(integration) === "mcp" ? (
    <McpConnection integration={integration} />
  ) : (
    <ApiConnection integration={integration} template={template} />
  );
};

/**
 * Servidor MCP (AB#87477): a URL e o modo de autenticação vêm do template e não mudam aqui;
 * o que a organização troca é o token (vazio = manter) ou a autorização OAuth.
 */
const McpConnection = ({ integration }: { integration: IntegrationBase }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fieldId = useId();
  const applyUpdated = useIntegrationBaseCache();
  const [token, setToken] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);

  const config = integration.mcp_config ?? {};
  const authMode = config.auth_mode ?? "none";
  const oauth = mcpOAuthOf(integration);

  const errorToast = (error: unknown) =>
    toast({
      title: t("common.error"),
      description: resolveApiErrorMessage(error, t),
      variant: "destructive",
    });

  const handleSaveToken = async () => {
    const credentials = mcpCredentials(authMode, token);
    if (!credentials) return;
    setIsSaving(true);
    try {
      const result = await IntegrationBaseService.update(integration.id, { credentials });
      await applyUpdated(result.integration, integration.id);
      setToken("");
      toast({ title: t("integrations.messages.authUpdated") });
    } catch (error) {
      errorToast(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReconnect = async () => {
    setIsReconnecting(true);
    try {
      const authorizationUrl = await IntegrationBaseService.startMcpOAuth(integration.id);
      // O callback volta para `?tab=connection&oauth=...` desta página.
      window.location.assign(authorizationUrl);
    } catch (error) {
      errorToast(error);
      setIsReconnecting(false);
    }
  };

  return (
    <Card>
      <CardContent className="max-w-xl space-y-6 pt-6">
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor={`${fieldId}-url`}>
            {t("integrations.mcp.serverUrl")}
          </label>
          <Input
            id={`${fieldId}-url`}
            value={config.server_url ?? ""}
            readOnly
            className="bg-muted font-mono text-xs"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">{t("integrations.mcp.authMode")}:</span>
          <Badge variant="secondary">{t(`integrations.mcp.authModes.${authMode}`)}</Badge>
          {config.tools_synced_at && (
            <span className="text-xs text-muted-foreground">
              {t("integrations.mcp.toolsSyncedAt", { date: new Date(config.tools_synced_at).toLocaleString() })}
            </span>
          )}
        </div>

        {needsToken(authMode) && (
          <div className="space-y-3">
            {authMode === "apikey" && (
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor={`${fieldId}-header`}>
                  {t("integrations.mcp.authHeader")}
                </label>
                <Input
                  id={`${fieldId}-header`}
                  value={config.auth_header || DEFAULT_API_KEY_HEADER}
                  readOnly
                  className="bg-muted font-mono text-xs"
                />
              </div>
            )}
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor={`${fieldId}-token`}>
                {authMode === "apikey" ? t("integrations.mcp.apiKey") : t("integrations.mcp.token")}
              </label>
              <PasswordField
                id={`${fieldId}-token`}
                field={{
                  name: "mcp_token",
                  label: t("integrations.mcp.token"),
                  type: "password",
                  placeholder: t("integrations.connection.keepCredential"),
                }}
                value={token}
                onChange={setToken}
              />
              <p className="text-xs text-muted-foreground">{t("integrations.connection.keepCredential")}</p>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => void handleSaveToken()} disabled={!token.trim() || isSaving}>
                {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                {t("integrations.actions.save")}
              </Button>
            </div>
          </div>
        )}

        {authMode === "oauth" && (
          <div className="space-y-3 rounded-lg border p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">{t("integrations.oauth.label")}:</span>
              <Badge variant={oauth?.status === "connected" ? "success" : "warning"}>
                {t(`integrations.oauth.status.${oauth?.status ?? "pending"}`)}
              </Badge>
              {oauth?.authorization_server_url && (
                <span className="truncate text-xs text-muted-foreground">{oauth.authorization_server_url}</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {oauth?.status === "connected" ? t("integrations.oauth.connectedHint") : t("integrations.oauth.pendingHint")}
            </p>
            <Button variant="outline" className="gap-1" onClick={() => void handleReconnect()} disabled={isReconnecting}>
              {isReconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {t("integrations.oauth.reconnect")}
            </Button>
          </div>
        )}

        {authMode === "none" && <p className="text-xs text-muted-foreground">{t("integrations.mcp.noAuthHint")}</p>}
      </CardContent>
    </Card>
  );
};

function initialApiValues(integration: IntegrationBase, settingsFields: FormField[], authFields: AuthField[]) {
  const values: Record<string, unknown> = {};
  for (const field of authFields) values[field.key] = "";
  for (const field of settingsFields) {
    if (field.readonly) continue;
    const formKey = (field.key || field.name) as string;
    const storageKey = field.name || field.key;
    const value = storageKey
      ? (integration.settings?.[storageKey] ?? integration.settings?.[formKey])
      : undefined;
    if (value !== undefined) values[formKey] = value;
  }
  if (settingsFields.some((field) => field.readonly && (field.key || field.name) === "webhook_url")) {
    const adapterType = integration.settings?.adapter_type as string | undefined;
    const baseUrl = window.location.origin;
    values.webhook_url =
      adapterType === "bot_framework"
        ? `${baseUrl}/api/communicator/botframework/${integration.id}/messages`
        : `${baseUrl}/api/communicator/webhook/${integration.id}`;
  }
  return values;
}

const ApiConnection = ({ integration, template }: IntegrationConnectionTabProps) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.split("-")[0];
  const { toast } = useToast();
  const applyUpdated = useIntegrationBaseCache();

  const translatedTemplate = useMemo(
    () => (template ? translateDeep(template, currentLang) : null),
    [template, currentLang],
  );
  const settingsFields = useMemo<FormField[]>(
    () => translatedTemplate?.settings?.form_fields ?? [],
    [translatedTemplate],
  );
  const authFields = useMemo<AuthField[]>(
    () => translatedTemplate?.auth_fields?.fields ?? [],
    [translatedTemplate],
  );
  const metadataFields = useMemo<MetadataFieldDefinition[]>(() => {
    const metadata = translatedTemplate?.metadata as Record<string, unknown> | undefined;
    const campos = Array.isArray(metadata?.form_fields) ? metadata!.form_fields : [];
    return (campos as MetadataFieldDefinition[]).filter((campo) => campo?.key && campo?.type);
  }, [translatedTemplate]);
  const hasModelsField = metadataFields.some((campo) => campo.type === "models");

  const [values, setValues] = useState<Record<string, unknown>>({});
  const [modelEntries, setModelEntries] = useState<ModelEntry[]>([]);
  const [metadataValues, setMetadataValues] = useState<Record<string, unknown>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [postInstall, setPostInstall] = useState<IntegrationPostInstall | null>(null);

  useEffect(() => {
    setValues(initialApiValues(integration, settingsFields, authFields));
    const gravado = (integration.metadata ?? {}) as Record<string, unknown>;
    setModelEntries(modelsToEntries(gravado.models));
    const { models: _models, ...demais } = gravado;
    setMetadataValues(demais);
  }, [integration, settingsFields, authFields]);

  const handleSave = async () => {
    const faltando = missingRequiredMetadata(metadataFields, metadataValues, modelEntries);
    if (faltando.length > 0) {
      toast({ title: t("common.error"), description: t("integrations.metadata.required"), variant: "destructive" });
      return;
    }
    const problemasDeModelo = validateEntries(modelEntries);
    if (problemasDeModelo.length > 0) {
      const detalhes = problemasDeModelo
        .slice(0, 3)
        .map((problema) =>
          problema.model
            ? t(`integrations.models.problem.${problema.reason}`, { model: problema.model })
            : t("integrations.models.problem.emptyName"),
        )
        .join(" · ");
      toast({ title: t("common.error"), description: detalhes, variant: "destructive" });
      return;
    }

    // Só vai a credencial digitada: vazio = manter a atual.
    const credentials: Record<string, unknown> = {};
    for (const field of authFields) {
      const value = values[field.key];
      if (value !== undefined && String(value).trim() !== "") credentials[field.key] = value;
    }
    const settings: Record<string, unknown> = {};
    for (const field of settingsFields) {
      if (field.readonly) continue; // campos readonly são computados, não persistidos
      const formKey = field.key || field.name;
      const storageKey = (field.name || field.key) ?? "";
      const value = values[formKey];
      if (value !== undefined && value !== "") settings[storageKey] = value;
    }

    setIsSaving(true);
    try {
      const result = await IntegrationBaseService.update(integration.id, {
        // Mesmo formato que o modal de edição mandava: só os valores dos campos do template.
        settings,
        ...(Object.keys(credentials).length > 0 ? { credentials } : {}),
        ...(metadataFields.length > 0
          ? {
              metadata: {
                ...metadataValues,
                ...(hasModelsField ? { models: entriesToModels(modelEntries) } : {}),
              },
            }
          : {}),
      });
      await applyUpdated(result.integration, integration.id);
      if (result.post_install) setPostInstall(result.post_install);
      else toast({ title: t("integrations.messages.saved") });
    } catch (error) {
      toast({
        title: t("common.error"),
        description: resolveApiErrorMessage(error, t),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!template) {
    return (
      <div className="flex min-h-[160px] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasFields = settingsFields.length > 0 || authFields.length > 0 || metadataFields.length > 0;

  return (
    <>
      <Card>
        <CardContent className="max-w-xl space-y-6 pt-6">
          {hasFields ? (
            <>
              <IntegrationConfigFields
                settingsFields={settingsFields}
                authFields={authFields}
                values={values}
                onChange={(key, value) => setValues((atual) => ({ ...atual, [key]: value }))}
                credentialHint={t("integrations.connection.keepCredential")}
              />
              {metadataFields.length > 0 && (
                <div className="space-y-3 border-t pt-4">
                  <h3 className="text-sm font-semibold text-muted-foreground">{t("integrations.metadata.tab")}</h3>
                  <IntegrationMetadataFields
                    fields={metadataFields}
                    values={metadataValues}
                    onValueChange={(key, value) => setMetadataValues((atual) => ({ ...atual, [key]: value }))}
                    modelEntries={modelEntries}
                    onModelEntriesChange={setModelEntries}
                  />
                </div>
              )}
              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  {t("integrations.actions.save")}
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{t("integrations.connection.noFields")}</p>
          )}
        </CardContent>
      </Card>

      {postInstall && (
        <IntegrationPostInstallDialog
          isOpen={true}
          onClose={() => setPostInstall(null)}
          integrationLabel={integration.label || integration.name}
          postInstall={postInstall}
        />
      )}
    </>
  );
};
